// Feb 28, 2026 — Provider tracker pill (single tile + redistribute popup).
//
// Shared between Dashboard → Operations (sum across all silos) and the
// in-silo OpsTab (per-silo with editable total). The two surfaces use
// the exact same visual format; the difference is whether the popup
// allows reconciliation overrides.
//
// Green state (BYOK set) → idle bucket dominates the tile, redistribute
// popup lets the user shuffle stranded funds + jump to provider console.
// Red state (no BYOK) → same tile shape with red accent + "Top up [prov]"
// CTA that routes into VoucherTopupModal locked to the provider.
import { useState, useEffect, useMemo, useCallback } from "react";
import { CheckCircle, ArrowSquareOut, Plus, ArrowsCounterClockwise, X, Equals, CaretDown, CaretUp, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtUSD = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const dollarsToCents = (s) => {
  const n = parseFloat(String(s).replace(/[^0-9.]/g, "")) || 0;
  return Math.max(0, Math.round(n * 100));
};

// Jun 1, 2026 — Money input you can actually type into. Local draft string so
// you can click in, clear the box, and type freely (e.g. "1.9"); it only
// reformats to 2 decimals + commits the cents value on blur or Enter. The old
// inline input forced (cents/100).toFixed(2) on every keystroke, which fought
// the cursor and made partial entry impossible.
function MoneyField({ cents, onCommitCents, unreconciled, testid }) {
  const fmt = (c) => (Number(c || 0) / 100).toFixed(2);
  const [draft, setDraft] = useState(fmt(cents));
  useEffect(() => { setDraft(fmt(cents)); }, [cents]);
  const commit = () => {
    const raw = String(draft).trim();
    if (raw === "" || raw === ".") { setDraft(fmt(0)); if (cents !== 0) onCommitCents(0); return; }
    const c = dollarsToCents(raw);
    setDraft(fmt(c));
    if (c !== cents) onCommitCents(c);
  };
  return (
    <input
      type="number" step="0.01" min="0"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
      data-testid={testid}
      className={`w-24 text-right tabular-nums text-sm bg-white border hairline rounded px-2 py-1 focus:outline-none focus:border-[#4A5D4E] ${unreconciled ? "text-[#C16E5A] border-[#E5BBA8]" : "text-[#1C1B1A]"}`}
    />
  );
}


export const PROVIDER_META = {
  gemini: {
    id: "gemini",
    label: "Gemini",
    consoleUrl: "https://aistudio.google.com/app/apikey",
    consoleLabel: "Google AI Studio",
  },
  anthropic: {
    id: "anthropic",
    label: "Anthropic",
    consoleUrl: "https://console.anthropic.com/",
    consoleLabel: "Anthropic console",
  },
  openai: {
    id: "openai",
    label: "OpenAI",
    consoleUrl: "https://platform.openai.com/",
    consoleLabel: "OpenAI dashboard",
  },
};

// Jun 2026 (Randy) — Tiny "peek" button that sits right next to the provider
// name inside any tracker pill. Its resting label is the CREATOR-WIDE total for
// this provider+mode (silo pills) or literally "ALL" (dashboard pills, whose big
// number is already the all-silos total). Click → a small read-only dropdown
// breaking the balance down per silo (current silo first + highlighted). It's a
// pop-anywhere micro-version of Recalibrate — view only, no edits.
function ProviderAllPeek({ provider, byokMode, focusSiloId, label, testidBase }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const meta = PROVIDER_META[provider];
  const field = byokMode ? `byok_self_balance_${provider}_cents` : `${provider}_voucher_cents`;

  const loadRows = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/voucher-summary");
      let list = (r.data?.silos || []).map((s) => ({ id: s.silo_id, name: s.silo_name, cents: Number(s[field] || 0) }));
      if (focusSiloId) list = [...list].sort((a, b) => (a.id === focusSiloId ? -1 : b.id === focusSiloId ? 1 : 0));
      setRows(list);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load silo breakdown");
    } finally { setLoading(false); }
  };

  const toggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next && !rows) loadRows();
  };

  const total = (rows || []).reduce((a, b) => a + b.cents, 0);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={toggle}
        data-testid={`${testidBase}-allpeek-toggle`}
        title="See this tracker's balance in every silo you own"
        className="text-[10px] leading-none tabular-nums px-1.5 py-0.5 rounded border hairline text-[#6B6A68] hover:text-[#1C1B1A] hover:border-[#4A5D4E] transition-colors"
      >
        {label}
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid={`${testidBase}-allpeek-backdrop`} />
          <div
            data-testid={`${testidBase}-allpeek-dropdown`}
            className="absolute z-50 left-0 top-6 w-60 bg-white border hairline rounded-md shadow-xl p-2 text-left"
          >
            <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.15em] text-[#6B6A68] mb-1 px-1">
              <span>{meta?.label} · all silos</span>
              {rows && <span className="tabular-nums text-[#1C1B1A] font-semibold">{fmtUSD(total)}</span>}
            </div>
            {loading && <div className="text-[11px] text-[#6B6A68] py-2 text-center">Loading…</div>}
            {!loading && rows && rows.length === 0 && (
              <div className="text-[11px] text-[#6B6A68] py-2 text-center">No silos yet.</div>
            )}
            {!loading && rows && rows.map((r) => {
              const isCurrent = r.id === focusSiloId;
              return (
                <div
                  key={r.id}
                  data-testid={`${testidBase}-allpeek-row-${r.id}`}
                  className={`flex items-center justify-between gap-2 px-1.5 py-1 rounded text-[11px] ${isCurrent ? "bg-[#E8EBE5] text-[#1C1B1A] font-semibold" : "text-[#3A3A38]"}`}
                >
                  <span className="truncate inline-flex items-center gap-1">
                    {isCurrent && <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A5D4E] shrink-0" title="Current silo" />}
                    {r.name}
                  </span>
                  <span className="tabular-nums shrink-0">{fmtUSD(r.cents)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </span>
  );
}

// Public: one tile rendered on the Dashboard ops row OR inside a silo OpsTab.
// Props:
//   provider       — "gemini" | "anthropic" | "openai"
//   byok           — boolean (creator has BYOK key for this provider)
//   bucketCents    — current bucket value displayed in the tile
//   spent30Cents   — last 30d platform spend on this provider (info chip)
//   onRedistributeClick(provider) — opens the redistribute popup
//   onTopupClick(provider)        — opens VoucherTopupModal locked to provider
//
// Render is the same shape regardless of mode (Green vs Red) — only the
// CTA and color swap. Per design rule #1, external links open in a new tab.
export function ProviderTrackerTile({ provider, byok, bucketCents, spent30Cents, onRedistributeClick, onTopupClick, peek = null }) {
  const meta = PROVIDER_META[provider];
  if (!meta) return null;
  // Jun 2026 (Randy) — "Redistribute" isn't intuitive for first-time setup.
  // Say "Setup" when the bucket is still at zero, "Redistribute" once funded.
  const redistLabel = (bucketCents || 0) <= 0 ? "Setup" : "Redistribute";
  const tone = byok
    ? "border-[#4A5D4E]/40 bg-[#E8EBE5]/30"
    : "border-[#E5BBA8] bg-[#FDF1ED]/40";
  const amountTone = byok ? "text-[#2F6B3A]" : "text-[#C16E5A]";
  const chipTone = byok
    ? "bg-[#E8EBE5] text-[#2F6B3A]"
    : "bg-[#FDF1ED] text-[#7A2A20]";
  const chipLabel = byok ? "BYOK · 1×" : "Platform · 3×";

  return (
    <div
      data-testid={`provider-tracker-${meta.id}`}
      data-byok={byok ? "true" : "false"}
      className={`border rounded-md p-3 flex flex-col gap-2 ${tone}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#1C1B1A] font-medium">
            {meta.label}
          </span>
          {peek && (
            <ProviderAllPeek
              provider={meta.id}
              byokMode={byok}
              focusSiloId={peek.focusSiloId || null}
              label={peek.context === "dashboard" ? "ALL" : fmtUSD(peek.totalCents || 0)}
              testidBase={`provider-tracker-${meta.id}`}
            />
          )}
        </div>
        <span className={`text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded inline-flex items-center gap-1 shrink-0 ${chipTone}`}>
          {byok && <CheckCircle size={9} weight="duotone" />}
          {chipLabel}
        </span>
      </div>

      {/* Idle bucket amount — dominant per design (Feb 28 redesign) */}
      <div
        data-testid={`provider-tracker-${meta.id}-amount`}
        className={`font-serif-display text-4xl leading-none tabular-nums ${amountTone}`}
      >
        {fmtUSD(bucketCents)}
      </div>

      <div className="text-[10px] text-[#6B6A68] tabular-nums leading-snug">
        Across all your silos · Spent last 30d: {fmtUSD(spent30Cents)}
      </div>

      <div className="flex items-center gap-2 mt-1 flex-wrap justify-between">
        {byok ? (
          <button
            type="button"
            onClick={() => onRedistributeClick?.(meta.id)}
            data-testid={`provider-tracker-${meta.id}-redistribute`}
            className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] inline-flex items-center gap-1"
          >
            <ArrowsCounterClockwise size={10} weight="bold" /> {redistLabel}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onTopupClick?.(meta.id)}
              data-testid={`provider-tracker-${meta.id}-topup`}
              className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1"
            >
              <Plus size={10} weight="bold" /> Top up {meta.label}
            </button>
            <button
              type="button"
              onClick={() => onRedistributeClick?.(meta.id)}
              data-testid={`provider-tracker-${meta.id}-redistribute`}
              className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#1C1B1A] text-[#1C1B1A] hover:bg-white inline-flex items-center gap-1"
            >
              <ArrowsCounterClockwise size={10} weight="bold" /> {redistLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Public: Redistribute popup. Pull a per-provider rollup of buckets across
// all of the user's silos. The popup is IDENTICAL whether opened from the
// Dashboard ops row or an in-silo OpsTab (per Feb 28 unification — same
// component, same flow, no watered-down variants). Total is always
// editable so the user can reconcile with the real provider dashboard;
// every silo row has an offset checkbox that shrinks-to-balance the
// others as that one grows.
//
// `focusSiloId` (optional) — which silo to default the offset checkbox to.
//   When opened from a silo OpsTab we default to "not this silo" so the
//   user adjusting this silo's number doesn't shrink itself; when opened
//   from the Dashboard we default to the first silo in the list.
export function RedistributePopup({ open, onClose, provider, focusSiloId = null, byokMode = false, onSaved }) {
  const meta = PROVIDER_META[provider] || null;
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [silos, setSilos] = useState([]);
  const [originalTotalCents, setOriginalTotalCents] = useState(0);
  const [totalStr, setTotalStr] = useState("");
  const [offsetSiloId, setOffsetSiloId] = useState(null);
  // Jun 2026 (Randy) — cross-LLM allocation tool (PLATFORM vouchers only).
  // Shuffle what's been paid in between Gemini/OpenAI/Anthropic with no new
  // charge. Collapsed by default; the moved chunk lands on the offset ("top")
  // silo, then the per-silo tool below lets the user spread it.
  const [providerTotals, setProviderTotals] = useState({ gemini: 0, anthropic: 0, openai: 0 });
  const [llmOpen, setLlmOpen] = useState(false);
  const [llmAmounts, setLlmAmounts] = useState({ gemini: 0, anthropic: 0, openai: 0 });
  const [shifting, setShifting] = useState(false);

  const loadData = useCallback(async () => {
    if (!provider) return;
    setLoading(true);
    try {
      const r = await api.get("/me/voucher-summary");
      // May 28, 2026 v2 — BYOK self-balance is now per-silo (mirrors platform
      // voucher arch). Same popup for BOTH modes; only difference: red total
      // is fixed, green total is editable.
      const fieldName = byokMode ? `byok_self_balance_${provider}_cents` : `${provider}_voucher_cents`;
      const rows = (r.data?.silos || []).map((s) => ({
        silo_id: s.silo_id,
        silo_name: s.silo_name,
        cents: Number(s[fieldName] || 0),
      }));
      setSilos(rows);
      const sum = rows.reduce((a, b) => a + b.cents, 0);
      setOriginalTotalCents(sum);
      setTotalStr((sum / 100).toFixed(2));
      // Jun 24, 2026 (Randy) — the offset ("−") marker always defaults to the
      // TOP silo (first row), regardless of which silo opened the modal, so any
      // remainder / total-recalibration lands there.
      const defaultOffset = rows[0]?.silo_id || null;
      setOffsetSiloId(defaultOffset);
      const pt = r.data?.provider_totals_cents || { gemini: 0, anthropic: 0, openai: 0 };
      setProviderTotals(pt);
      setLlmAmounts({ gemini: Number(pt.gemini || 0), anthropic: Number(pt.anthropic || 0), openai: Number(pt.openai || 0) });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load buckets");
    } finally {
      setLoading(false);
    }
  }, [provider, byokMode, focusSiloId]);

  useEffect(() => {
    if (!open || !provider) return;
    setLlmOpen(false);
    loadData();
  }, [open, provider, loadData]);

  const currentSum = useMemo(() => silos.reduce((a, b) => a + b.cents, 0), [silos]);
  // Green mode honors the user-typed total (it's their self-reported
  // balance at the LLM studio). Red mode locks the total to the snapshot
  // — they can't claim to have more than they paid in.
  const targetTotalCents = byokMode ? dollarsToCents(totalStr) : originalTotalCents;
  const diff = currentSum - targetTotalCents;
  const unreconciled = diff !== 0;
  // Red coloring only fires in BYOK mode when the typed total differs
  // from the loaded snapshot. Red-mode platform pills never go coral.
  const overrideActive = byokMode && (targetTotalCents !== originalTotalCents);

  const setSiloCents = (sid, newCents) => {
    newCents = Math.max(0, newCents);
    if (offsetSiloId && offsetSiloId !== sid) {
      // Offset mode: when user adjusts one silo, the offset silo absorbs
      // the inverse delta so the total stays equal to the user's target.
      setSilos((prev) => {
        const old = prev.find((p) => p.silo_id === sid)?.cents || 0;
        const delta = newCents - old;
        return prev.map((row) => {
          if (row.silo_id === sid) return { ...row, cents: newCents };
          if (row.silo_id === offsetSiloId) {
            return { ...row, cents: Math.max(0, row.cents - delta) };
          }
          return row;
        });
      });
    } else {
      setSilos((prev) => prev.map((row) => (row.silo_id === sid ? { ...row, cents: newCents } : row)));
    }
  };

  const redistributeEvenly = () => {
    if (!silos.length) return;
    const target = targetTotalCents;
    const share = Math.floor(target / silos.length);
    const rem = target - share * silos.length;
    // Jun 24, 2026 (Randy) — drop the rounding remainder on the offset ("−")
    // silo (top by default) so it always lands wherever the negative sign is.
    const offsetIdx = silos.findIndex((s) => s.silo_id === offsetSiloId);
    const remIdx = offsetIdx >= 0 ? offsetIdx : 0;
    setSilos((prev) => prev.map((row, i) => ({ ...row, cents: share + (i === remIdx ? rem : 0) })));
  };

  // Jun 24, 2026 (Randy) — when the editable total is re-typed and the field
  // loses focus, auto-recalibrate: the offset ("−", top by default) silo
  // absorbs the entire difference so allocations stay reconciled with no manual
  // "Distribute evenly" step.
  const absorbTotalIntoOffset = () => {
    const newTarget = dollarsToCents(totalStr);
    setTotalStr((newTarget / 100).toFixed(2));
    if (!offsetSiloId || !silos.length) return;
    setSilos((prev) => {
      const others = prev
        .filter((r) => r.silo_id !== offsetSiloId)
        .reduce((a, b) => a + b.cents, 0);
      return prev.map((row) =>
        row.silo_id === offsetSiloId ? { ...row, cents: Math.max(0, newTarget - others) } : row,
      );
    });
  };

  // --- Per-LLM (cross-provider) shift helpers — platform vouchers only ---
  const otherProviders = useMemo(
    () => ["gemini", "openai", "anthropic"].filter((p) => p !== provider),
    [provider],
  );
  const llmGrandTotal = useMemo(
    () => (providerTotals.gemini || 0) + (providerTotals.anthropic || 0) + (providerTotals.openai || 0),
    [providerTotals],
  );
  const llmChanged = useMemo(
    () => ["gemini", "anthropic", "openai"].some((p) => (llmAmounts[p] || 0) !== (providerTotals[p] || 0)),
    [llmAmounts, providerTotals],
  );
  const offsetSiloName = useMemo(
    () => silos.find((s) => s.silo_id === offsetSiloId)?.silo_name || "your top silo",
    [silos, offsetSiloId],
  );
  // Edit either of the OTHER two providers; the current provider (the modal's
  // source) absorbs the remainder so the grand total never changes.
  const setLlmTarget = (p, cents) => {
    cents = Math.max(0, cents);
    setLlmAmounts((prev) => {
      const next = { ...prev, [p]: cents };
      let othersSum = otherProviders.reduce((a, k) => a + (next[k] || 0), 0);
      if (othersSum > llmGrandTotal) {
        next[p] = Math.max(0, cents - (othersSum - llmGrandTotal));
        othersSum = otherProviders.reduce((a, k) => a + (next[k] || 0), 0);
      }
      next[provider] = Math.max(0, llmGrandTotal - othersSum);
      return next;
    });
  };

  const applyLlmShift = async () => {
    if (!offsetSiloId) { toast.error("No silo to land the funds in."); return; }
    if (!llmChanged) return;
    setShifting(true);
    try {
      await api.post("/me/voucher/shift-provider", {
        landing_silo_id: offsetSiloId,
        targets: {
          gemini: llmAmounts.gemini || 0,
          anthropic: llmAmounts.anthropic || 0,
          openai: llmAmounts.openai || 0,
        },
      });
      toast.success("Funds moved between LLM buckets.");
      await loadData();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move funds");
    } finally {
      setShifting(false);
    }
  };

  const save = async () => {
    if (unreconciled) {
      toast.error("Allocations must sum to the total. Use the checkbox to absorb the difference.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/voucher/redistribute", {
        provider,
        mode: byokMode ? "byok" : "platform",
        allocations: silos.map((s) => ({ silo_id: s.silo_id, cents: s.cents })),
        // Backend only honors total_override_cents in byok mode (platform
        // mode is always locked to the current paid-in sum).
        ...(byokMode && overrideActive ? { total_override_cents: targetTotalCents } : {}),
      });
      toast.success(`${meta?.label || provider} buckets rebalanced.`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !meta) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid={`redistribute-modal-${meta.id}`}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90dvh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b hairline shrink-0">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Redistribute</div>
            <div className="font-serif-display text-xl text-[#1C1B1A] leading-tight">{meta.label} bucket</div>
          </div>
          <button onClick={onClose} className="text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="redistribute-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="bg-[#F0F1EE] border hairline rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68]">
              {byokMode
                ? `Your balance at ${meta.consoleLabel} (editable)`
                : (silos.length > 1 ? "Total across all silos (fixed)" : "Total (fixed)")}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {byokMode ? (
                <div className="flex items-center gap-1">
                  <span className={`text-2xl font-serif-display ${overrideActive ? "text-[#C16E5A]" : "text-[#4A5D4E]"}`}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={totalStr}
                    onChange={(e) => setTotalStr(e.target.value)}
                    onBlur={absorbTotalIntoOffset}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                    data-testid="redistribute-total-input"
                    className={`font-serif-display text-2xl tabular-nums w-32 bg-transparent border-b-2 focus:outline-none ${overrideActive ? "border-[#C16E5A] text-[#C16E5A] focus:border-[#C16E5A]" : "border-[#4A5D4E] text-[#4A5D4E] focus:border-[#1C1B1A]"}`}
                  />
                </div>
              ) : (
                <div className="font-serif-display text-2xl text-[#1C1B1A] tabular-nums" data-testid="redistribute-total-fixed">
                  ${(originalTotalCents / 100).toFixed(2)}
                </div>
              )}
              {silos.length > 1 && (
                <button
                  type="button"
                  onClick={redistributeEvenly}
                  data-testid="redistribute-evenly"
                  className="ml-auto text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border border-[#4A5D4E] text-[#4A5D4E] hover:bg-[#E8EBE5] inline-flex items-center gap-1"
                >
                  <Equals size={10} weight="bold" /> Distribute evenly
                </button>
              )}
            </div>
            <div className="text-[10px] text-[#6B6A68] mt-2 leading-snug">
              {byokMode
                ? `Whenever you check your real ${meta.consoleLabel} dashboard, re-type the total here. Then use "Distribute evenly" or the offset checkboxes to split it across your silos. Every BYOK call drops the matching silo's slice by the raw cost.`
                : (silos.length > 1
                    ? `Shuffle what you've already paid in across your silos. The total is locked — to add more, click "Top up ${meta.label}" on the pill.`
                    : "Only one silo to allocate to — your current platform voucher.")}
            </div>
          </div>

          {byokMode ? (
            /* GREEN / BYOK — the bucket IS your real balance at the provider,
               so the only way to add funds is at the provider's own site. */
            <a
              href={meta.consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`redistribute-console-link-${meta.id}`}
              className="block bg-[#1C1B1A] text-[#FDFCF8] rounded-md p-3 hover:bg-[#3A4A3E] transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#FDFCF8]/70">Top up at the source</div>
                  <div className="text-sm font-medium">Open {meta.consoleLabel}</div>
                </div>
                <ArrowSquareOut size={14} weight="bold" />
              </div>
              <div className="text-[10px] text-[#FDFCF8]/70 mt-1">
                Adds credit to your actual {meta.label} account. Come back and update the total above.
              </div>
            </a>
          ) : (
            /* RED / PLATFORM voucher — Jun 2026 (Randy): no pointless provider
               links here. Instead, a collapsible tool to shuffle prepaid funds
               between the three LLM buckets (no new charge). */
            <div className="border hairline rounded-md overflow-hidden" data-testid="per-llm-allocations">
              <button
                type="button"
                onClick={() => setLlmOpen((o) => !o)}
                data-testid="per-llm-toggle"
                className="w-full flex items-center justify-between p-3 bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] transition-colors"
              >
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#FDFCF8]/70">Per-LLM allocations</div>
                  <div className="text-sm font-medium">Move funds between Gemini · OpenAI · Anthropic</div>
                </div>
                {llmOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
              </button>
              {llmOpen && (
                <div className="p-3 space-y-2 bg-white">
                  <div className="text-[10px] text-[#6B6A68] leading-snug">
                    Shuffle what you've already paid in — no new charge. Edit the other two; <strong>{meta.label}</strong> (your source) absorbs the difference. Moved funds land in <strong>{offsetSiloName}</strong>; use "Per-silo allocations" below to spread them. To add new money, use "Top up".
                  </div>
                  {["gemini", "openai", "anthropic"].map((p) => {
                    const pm = PROVIDER_META[p];
                    const isSource = p === provider;
                    return (
                      <div
                        key={p}
                        data-testid={`per-llm-row-${p}`}
                        className={`flex items-center gap-2 rounded-md p-2 border hairline ${isSource ? "bg-[#FDF1ED]" : "bg-[#F0F1EE]/60"}`}
                      >
                        <span
                          title={isSource ? "Source — absorbs the difference" : "Target"}
                          className={`w-5 h-5 rounded border-2 inline-flex items-center justify-center text-[12px] font-bold shrink-0 ${isSource ? "border-[#C16E5A] bg-[#C16E5A] text-white" : "border-[#C8C7C3] text-[#C8C7C3]"}`}
                        >
                          {isSource ? "−" : "+"}
                        </span>
                        <div className="flex-1 min-w-0 text-sm font-medium text-[#1C1B1A] truncate">
                          {pm.label}
                          {isSource && <span className="text-[10px] text-[#6B6A68] ml-1 normal-case">(source)</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-sm text-[#6B6A68]">$</span>
                          {isSource ? (
                            <span
                              data-testid={`per-llm-amount-${p}`}
                              className="w-24 text-right tabular-nums text-sm px-2 py-1 text-[#C16E5A] font-medium"
                            >
                              {((llmAmounts[p] || 0) / 100).toFixed(2)}
                            </span>
                          ) : (
                            <MoneyField
                              cents={llmAmounts[p] || 0}
                              onCommitCents={(c) => setLlmTarget(p, c)}
                              testid={`per-llm-input-${p}`}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-xs tabular-nums px-3 py-2 rounded bg-[#E8EBE5] text-[#2F6B3A] flex items-center gap-1">
                    <Equals size={11} weight="bold" /> Total across LLMs stays {fmtUSD(llmGrandTotal)} (fixed)
                  </div>
                  <button
                    type="button"
                    onClick={applyLlmShift}
                    disabled={shifting || !llmChanged}
                    data-testid="per-llm-apply"
                    className="w-full py-2 text-xs uppercase tracking-[0.15em] rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 inline-flex items-center justify-center gap-1"
                  >
                    {shifting ? "Moving…" : <><ArrowRight size={11} weight="bold" /> Apply transfer</>}
                  </button>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-[#6B6A68] py-6 text-center">Loading silos…</div>
          ) : (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68]">Per-silo allocations</div>
              {silos.length === 0 && (
                <div className="text-sm text-[#6B6A68] py-3 text-center">No silos yet.</div>
              )}
              {silos.map((s) => (
                <div
                  key={s.silo_id}
                  data-testid={`redistribute-row-${s.silo_id}`}
                  className="flex items-center gap-2 bg-[#F0F1EE]/60 border hairline rounded-md p-2"
                >
                  <label className="inline-flex items-center cursor-pointer" title="Offset silo — absorbs the delta as you adjust the others">
                    <input
                      type="radio"
                      name="offset-silo"
                      checked={offsetSiloId === s.silo_id}
                      onChange={() => setOffsetSiloId(s.silo_id)}
                      data-testid={`redistribute-offset-${s.silo_id}`}
                      className="sr-only peer"
                    />
                    <span className="w-5 h-5 rounded border-2 border-[#C16E5A] peer-checked:bg-[#C16E5A] peer-checked:text-white inline-flex items-center justify-center text-[12px] font-bold">
                      {offsetSiloId === s.silo_id ? "−" : " "}
                    </span>
                  </label>
                  <div className="flex-1 min-w-0 text-sm font-medium text-[#1C1B1A] truncate">
                    {s.silo_name}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-sm ${unreconciled ? "text-[#C16E5A]" : "text-[#6B6A68]"}`}>$</span>
                    <MoneyField
                      cents={s.cents}
                      onCommitCents={(c) => setSiloCents(s.silo_id, c)}
                      unreconciled={unreconciled}
                      testid={`redistribute-input-${s.silo_id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={`text-xs tabular-nums px-3 py-2 rounded ${diff === 0 ? "bg-[#E8EBE5] text-[#2F6B3A]" : "bg-[#FDF1ED] text-[#7A2A20]"}`}>
            Sum of allocations: {fmtUSD(currentSum)} · target: {fmtUSD(targetTotalCents)}
            {diff !== 0 && ` · off by ${fmtUSD(Math.abs(diff))} ${diff > 0 ? "(too much)" : "(short)"}`}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t hairline shrink-0 pb-[calc(1rem_+_env(safe-area-inset-bottom))]">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="redistribute-cancel"
            className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || loading || unreconciled}
            data-testid="redistribute-save"
            className="px-4 py-2 text-xs uppercase tracking-[0.15em] rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save allocations"}
          </button>
        </div>
      </div>
    </div>
  );
}
